<script lang="ts" setup>
import { storeToRefs } from "pinia";

defineProps<{
  originalFileUrl?: string;
  isGoogleDriveFile?: boolean;
}>();

const documentId = inject("documentId") as any;
const documentHistoryStore = useDocumentHistoryStore(documentId);
const { canRedo, canUndo, isRestoringState } =
  storeToRefs(documentHistoryStore);

const {
  doesSelectionContainFormatting,
  formatSelectedTokens,
  selectedColor,
  selectedHighlightColor,
  doSelectedParagraphsHaveAlignment,
  alignSelectedParagraphs,
  indentSelectedParagraphs,
  unindentSelectedParagraphs,
} = useTokenFormatter();

const documentShowFormattingStore = useDocumentShowFormattingStore(documentId);
const { shouldShowFormatting } = storeToRefs(documentShowFormattingStore);

const documentZoomControl = useDocumentZoomControllStore(documentId);
const { zoomFactor } = storeToRefs(documentZoomControl);

const appUserData = useAppUserDataStore();
const { isUserProductReviewer } = storeToRefs(appUserData);

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { revision } = storeToRefs(submittedDocumentStore);

const checklistStore = useDocumentChecklistStore(documentId);
const { useChecklist } = storeToRefs(checklistStore);

const checklistController = useChecklistControllerStore(documentId);
const { currentChecklistCriteriaInstance, checklistControllerMode } =
  storeToRefs(checklistController);

const reportProblem = async (
  description: string,
  isControl: boolean,
  selectedTokens: ParagraphTokens
) => {
  await useReportEvaluationProblem(
    documentId,
    revision.value?.id ?? "",
    useChecklist.value?.id ?? "",
    useChecklist.value?.title ?? "",
    {
      selectedTokens,
    },
    description,
    isControl,
    currentChecklistCriteriaInstance.value?.checklistCriteriaId ?? "",
    checklistStore.getCriteriaFromId(
      currentChecklistCriteriaInstance.value?.checklistCriteriaId ?? ""
    )
  );

  useBaseToast("Problem reported", "success");
};

const isZoomable = computed(() => {
  if (
    revision.value?.fileObject != undefined ||
    (revision.value?.fileObjects != undefined &&
      revision.value.fileObjects.length > 0)
  ) {
    return false;
  }
  return true;
});
</script>

<template>
  <div
    class="rounded-md border bg-white h-8 box-content flex items-center justify-between px-1.5 py-1 shadow-sm"
    data-step-id="toolbar-container"
  >
    <div class="flex shrink-0 items-center gap-0.5 text-primary-default">
      <ToolbarTextColorButton
        compact
        :width="32"
        :height="32"
        data-step-id="toolbar-text-color-button"
        :selected-color="selectedColor(documentId)"
        @select-color="
          ($event) =>
            formatSelectedTokens(documentId, {
              color: $event,
            })
        "
        v-tooltip.bottom="'Font Color'"
      />
      <ToolbarTextHighlightButton
        compact
        :width="32"
        :height="32"
        :size="16"
        data-step-id="toolbar-highlight-button"
        :selected-color="selectedHighlightColor(documentId)"
        @select-color="
          ($event) =>
            formatSelectedTokens(documentId, {
              highlightColor: $event,
            })
        "
        v-tooltip.bottom="'Highlight Color'"
      />
      <ToolbarButton
        compact
        data-step-id="toolbar-strike-through-button"
        iconName="tabler:strikethrough"
        :is-selected="
          doesSelectionContainFormatting(documentId, 'strikeThrough')
        "
        @mousedown.prevent.stop="
          formatSelectedTokens(documentId, {
            strikeThrough: !doesSelectionContainFormatting(
              documentId,
              'strikeThrough'
            ),
          })
        "
        v-tooltip.bottom="'Strikethrough'"
      />
      <ToolbarButton
        compact
        data-step-id="toolbar-formatting-button"
        iconName="fa-solid:paragraph"
        :is-selected="shouldShowFormatting"
        @mousedown.prevent.stop="
          documentShowFormattingStore.toggleShouldShowFormatting()
        "
        v-tooltip.bottom="'Formatting'"
      />
      <DocumentFormattingLegendButton v-if="shouldShowFormatting" />
    </div>

    <div class="flex flex-row items-center">
      <span v-if="isUserProductReviewer == true">
        <BasePopover align="center">
          <BaseIconButton
            :size="32"
            class="h-8 w-8"
            tooltip="Flag for Review"
          >
            <icon name="material-symbols:flag" size="16" />
          </BaseIconButton>
          <template #content>
            <EvaluationResultsReportProblemForm
              :document-id="documentId"
              @report-problem="reportProblem"
            />
          </template>
        </BasePopover>
      </span>
      <div v-if="isZoomable" class="flex flex-row items-center">
        <TransitionFade v-if="zoomFactor != 1">
          <BaseTextButton
            @click="documentZoomControl.setZoomFactor(1)"
            class="text-xs"
            color="danger"
          >
            Reset Zoom
          </BaseTextButton>
        </TransitionFade>
        <BaseIconButton
          :size="32"
          @click.prevent.stop="documentZoomControl.zoomOut()"
          class="h-8 w-8"
          tooltip="Zoom Out"
        >
          <icon name="tabler:zoom-out" size="16" />
        </BaseIconButton>
        <span class="text-xs mx-1 select-none w-[35px] text-center"
          >{{ (zoomFactor * 100).toFixed(0) }}%</span
        >
        <BaseIconButton
          :size="32"
          @click.prevent.stop="documentZoomControl.zoomIn()"
          class="h-8 w-8"
          tooltip="Zoom In"
        >
          <icon name="tabler:zoom-in" size="16" />
        </BaseIconButton>
      </div>
      <div class="flex flex-row relative">
        <BaseIconButton
          :size="32"
          @click="documentHistoryStore.undo"
          class="h-8 w-8"
          tooltip="Undo"
          :disabled="canUndo != true"
        >
          <icon name="ci:undo" size="16" />
        </BaseIconButton>

        <BaseIconButton
          :size="32"
          @click="documentHistoryStore.redo"
          class="h-8 w-8"
          tooltip="Redo"
          :disabled="canRedo != true"
        >
          <icon name="ci:redo" size="16" />
        </BaseIconButton>
        <div
          v-if="isRestoringState"
          class="flex rounded items-center justify-center absolute top-0 left-0 w-full h-full bg-medium-default bg-opacity-20"
        >
          <icon name="svg-spinners:ring-resize" />
        </div>
      </div>
      <div v-if="originalFileUrl" class="ml-1 flex shrink-0 items-center border-l border-medium-default/20 pl-1">
        <a
          :href="originalFileUrl"
          target="_blank"
          rel="noopener noreferrer"
          v-tooltip.bottom="isGoogleDriveFile ? 'Open original in Google Drive' : 'Download original Word document'"
          :aria-label="isGoogleDriveFile ? 'Open original in Google Drive' : 'Download original Word document'"
          class="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded px-2 text-xs text-primary-default transition-colors duration-150 hover:bg-medium-default/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default"
        >
          <icon :name="isGoogleDriveFile ? 'tabler:external-link' : 'tabler:download'" size="16" />
          <span>{{ isGoogleDriveFile ? 'Open original' : 'Original' }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
